// client/src/stores/auth-store.ts — the in-memory session · AUTH-004 (04 §9.5).
//
// §9.5's row for this store: "in-memory access token + decoded claims (userId, org_id, role,
// permissions), session status, active-org display info". Explicitly NOT here: the refresh
// token — it is an `HttpOnly` cookie the browser sends on its own and JavaScript never sees
// (02 §6.2) — and any organization data.
//
// Nothing persists. A hard refresh starts from `status: 'unknown'` and re-authenticates through
// the refresh cookie (`src/session/source.ts`); an access token in localStorage would be a
// token any script on the page could read, which is the whole reason it lives here.
//
// The store knows nothing about wire shapes (§9.5 "stores know nothing about API DTOs"):
// `Session` and `PendingMfa` are the shell's own types from `src/session/types.ts`, and the
// source is what maps a response onto them.

import { create } from 'zustand';

import type { PendingMfa, Session } from '@/session/types';

/**
 * `unknown` until the first bootstrap has run — a guard that read `null` before the refresh
 * cookie had been tried would bounce every hard refresh to the login page.
 */
export type AuthStatus = 'unknown' | 'anonymous' | 'authenticated';

interface AuthState {
  status: AuthStatus;
  /** The bearer token, or undefined while anonymous. Read at request time, never copied. */
  accessToken: string | undefined;
  /** Epoch milliseconds at which `accessToken` stops being accepted. */
  accessTokenExpiresAt: number | undefined;
  /** The signed-in user, or null while anonymous. Replaced whole on every change. */
  session: Session | null;
  /** A login stopped at the second factor; cleared when it completes or the user starts over. */
  pendingMfa: PendingMfa | null;

  setAuthenticated: (accessToken: string, expiresAtUtc: string, session: Session) => void;
  /** A refresh swapped the token; the identity is unchanged (04 §9.3.3 "invisibly"). */
  setAccessToken: (accessToken: string, expiresAtUtc: string) => void;
  setAnonymous: () => void;
  setPendingMfa: (pending: PendingMfa | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'unknown',
  accessToken: undefined,
  accessTokenExpiresAt: undefined,
  session: null,
  pendingMfa: null,

  setAuthenticated: (accessToken, expiresAtUtc, session) =>
    set({
      status: 'authenticated',
      accessToken,
      accessTokenExpiresAt: Date.parse(expiresAtUtc),
      session,
      pendingMfa: null,
    }),

  setAccessToken: (accessToken, expiresAtUtc) =>
    set({ accessToken, accessTokenExpiresAt: Date.parse(expiresAtUtc) }),

  // The whole object goes, token included: `logoutHard()` and a failed refresh both land here,
  // and neither may leave a token behind for the next request to pick up.
  setAnonymous: () =>
    set({
      status: 'anonymous',
      accessToken: undefined,
      accessTokenExpiresAt: undefined,
      session: null,
    }),

  setPendingMfa: (pending) => set({ pendingMfa: pending }),
}));
